<template>
  <div id="summary-info-tab">
    <table>
      <thead>
        <tr>
          <th></th>
          <th>{{ $t('LOAN_AMOUNT') }}</th>
          <th>{{ $t('PAID') }}</th>
          <th>{{ $t('WAIVED') }}</th>
          <th>{{ $t('WRITTEN_OFF') }}</th>
          <th>{{ $t('OUTSTANDING') }}</th>
          <th>{{ $t('OVER_DUE') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td class="bold">{{ $t('PRINCIPAL') }}</td>
          <td>{{ addCommas(summaryData.principalDisbursed) }}</td>
          <td>{{ addCommas(summaryData.principalPaid) }}</td>
          <td></td>
          <td>{{ addCommas(summaryData.principalWrittenOff) }}</td>
          <td>{{ addCommas(summaryData.principalOutstanding) }}</td>
          <td>{{ addCommas(summaryData.principalOverdue) }}</td>
        </tr>
        <tr>
          <td class="bold">{{ $t('INTEREST') }}</td>
          <td>{{ addCommas(summaryData.interestCharged) }}</td>
          <td>{{ addCommas(summaryData.interestPaid) }}</td>
          <td>{{ addCommas(summaryData.interestWaived) }}</td>
          <td>{{ addCommas(summaryData.interestWrittenOff) }}</td>
          <td>{{ addCommas(summaryData.interestOutstanding) }}</td>
          <td>{{ addCommas(summaryData.interestOverdue) }}</td>
        </tr>
        <tr>
          <td class="bold">{{ $t('FEE') }}</td>
          <td>{{ addCommas(summaryData.feeChargesCharged) }}</td>
          <td>{{ addCommas(summaryData.feeChargesPaid) }}</td>
          <td>{{ addCommas(summaryData.feeChargesWaived) }}</td>
          <td>{{ addCommas(summaryData.feeChargesWrittenOff) }}</td>
          <td>{{ addCommas(summaryData.feeChargesOutstanding) }}</td>
          <td>{{ addCommas(summaryData.feeChargesOverdue) }}</td>
        </tr>
        <tr>
          <td class="child">{{ $t('STORAGE_FEE') }}</td>
          <td></td>
          <td>{{ addCommas(summaryData.warehousingFee) }}</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
        <tr>
          <td class="child">{{ $t('VALUE_ADJUSTMENT') }}</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td>{{ addCommas(summaryData.diffAmount) }}</td>
          <td></td>
        </tr>
        <tr>
          <td class="bold">{{ $t('OVERDUE_FEE') }}</td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td>{{ addCommas(summaryData.dueInterest) }}</td>
          <td>{{ addCommas(summaryData.dueInterest) }}</td>
        </tr>
        <tr>
          <td class="bold">{{ $t('TOTAL') }}</td>
          <td>{{ addCommas(summaryData.totalExpectedRepayment) }}</td>
          <td>{{ addCommas(summaryData.totalPaid) }}</td>
          <td>{{ addCommas(summaryData.totalWaived) }}</td>
          <td>{{ addCommas(summaryData.totalWrittenOff) }}</td>
          <td>{{ addCommas(summaryData.totalOutstanding) }}</td>
          <td></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { computed, onMounted } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { addCommas } from '@/helpers';

const { useState, useActions } = createNamespacedHelpers('contractDetail');

export default {
  name: 'SummaryInfoTab',
  setup(_, { root }) {
    const getSummaryFunction = (summaryState) => {
      if (summaryState === null) {
        return () => '';
      }
      return (name) => (summaryState[name] !== null ? summaryState[name] : '');
    };

    const { summary } = useState(['summary']);

    const summaryData = computed(() => {
      const get = getSummaryFunction(summary.value);

      return {
        principalDisbursed: get('principalDisbursed'),
        principalPaid: get('principalPaid'),
        principalWrittenOff: get('principalWrittenOff'),
        principalOutstanding: get('principalOutstanding'),
        principalOverdue: get('principalOverdue'),
        interestCharged: get('interestCharged'),
        interestPaid: get('interestPaid'),
        interestWaived: get('interestWaived'),
        interestWrittenOff: get('interestWrittenOff'),
        interestOutstanding: get('interestOutstanding'),
        interestOverdue: get('interestOverdue'),
        feeChargesCharged: get('feeChargesCharged'),
        feeChargesPaid: get('feeChargesPaid'),
        feeChargesWaived: get('feeChargesWaived'),
        feeChargesWrittenOff: get('feeChargesWrittenOff'),
        feeChargesOutstanding: get('feeChargesOutstanding'),
        feeChargesOverdue: get('feeChargesOverdue'),
        warehousingFee: get('warehousingFee'),
        diffAmount: get('diffAmount'),
        dueInterest: get('dueInterest'),
        totalExpectedRepayment: get('totalExpectedRepayment'),
        totalPaid: get('totalPaid'),
        totalWaived: get('totalWaived'),
        totalWrittenOff: get('totalWrittenOff'),
        totalOutstanding: get('totalOutstanding')
      };
    });

    const { getContractSummaryByMifosId } = useActions(['getContractSummaryByMifosId']);

    onMounted(() => {
      const { id } = root.$route.params;
      getContractSummaryByMifosId(id);
    });

    return {
      summaryData,
      addCommas,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/scss/colors";

#summary-info-tab {
  padding-top: 50px;
  border-radius: 3px;

  table {
    width: 100%;
    border-collapse: inherit;
    border-spacing: 0;

    thead {
      text-transform: uppercase;
      font-weight: bold;
      text-align: center;

      tr {

        th {
          background-color: $brightMaroon;
          color: $white;
          padding: 15px 10px;
          border-top: 1px solid $alto;
          border-bottom: 1px solid $alto;

          &:first-child {
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
            border-left: 1px solid $alto;

          }

          &:last-child {
            border-top-right-radius: 3px;
            border-bottom-right-radius: 3px;
            border-right: 1px solid $alto;
          }
        }
      }
    }

    tbody {
      text-align: center;

      tr {
        background-color: $babyPowder;
        transition: all .2s ease-in-out;

        &:hover {
          background-color: $ghostWhite;
        }

        &:last-child {
          font-weight: bold;
          text-transform: uppercase;
        }

        td {
          padding: 15px 5px;
          border-top: 1px solid $alto;
          border-bottom: 1px solid $alto;

          &:first-child {
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
            border-left: 1px solid $alto;
            padding-left: 15px;
            padding-right: 0px;
            text-align: left;
          }

          &:last-child {
            border-top-right-radius: 3px;
            border-bottom-right-radius: 3px;
            border-right: 1px solid $alto;
          }

          &.child {
            padding-left: 25px;
          }
        }
      }
    }

    .bold {
      font-weight: bold;
    }
  }
}
</style>
